import * as Sentry from '@sentry/react';
import type { ImportBatchDetails, ImportBatchSummary, ImportDiscardedRow, ImportPreviewResult, ImportPreviewRow, ImportPreviewSummary, ImportReconciliationMapping, ImportRowCorrection } from '../types/imports';
import type {
    AccessProfile,
    ConviteDaEmpresa,
    ConvitePublico,
    EffectivePermissionsMap,
    EmpresaDoUsuario,
    MembroDaEmpresa,
    PermissionItem,
} from '../types';
import { EMPRESA_ATIVA_KEY, empresaSalva } from '../contexts/empresaAtiva';
import {
    RequestTimeoutError,
    classifyNetworkFailure,
    isBrowserOnline,
    networkFailureFingerprint,
    shouldReportNetworkFailure,
} from './networkFailure';
import { safeLocalStorage } from '../utils/safeStorage';

// @ts-ignore - Vite env
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000/api';

const REQUEST_TIMEOUT_MS = 15000;
// Acima do timeout de 120s da transacao no backend: com o limite padrao, a tela
// acusaria erro numa planilha grande enquanto a importacao segue no servidor.
const IMPORT_CONFIRM_TIMEOUT_MS = 150000;
const MAX_RETRIES = 2; // so para GET (idempotente)
const RETRY_BASE_DELAY_MS = 400;
const RETRYABLE_STATUS = new Set([408, 502, 503, 504]);
const CIRCUIT_FAILURE_THRESHOLD = 3;
const CIRCUIT_COOLDOWN_MS = 20000;

// Fallback local: quando o backend esta totalmente inacessivel (rede caiu,
// circuito aberto), GETs recentes servem dados em cache em vez de quebrar a
// tela. Nao se aplica a POST/PUT/PATCH/DELETE (nao faz sentido "cachear" uma
// escrita). Expira em 30min para nao mostrar numero financeiro velho demais
// como se fosse atual.
const CACHE_PREFIX = 'api_cache:';
const CACHE_MAX_AGE_MS = 30 * 60 * 1000;

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function backoffDelay(attempt: number): number {
    return RETRY_BASE_DELAY_MS * 2 ** (attempt - 1) + Math.floor(Math.random() * 150);
}

/**
 * Erro de rede "amigavel" (API fora do ar, DNS, timeout, CORS ou circuit
 * breaker aberto). `friendlyMessage` e seguro para mostrar ao usuario;
 * `cause` guarda o erro original para debug/Sentry.
 */
export class ApiNetworkError extends Error {
    friendlyMessage: string;
    cause?: unknown;

    constructor(cause: unknown, friendlyMessage = 'Não foi possível conectar ao servidor. Verifique sua internet e tente novamente.') {
        super(cause instanceof Error ? cause.message : String(friendlyMessage));
        this.name = 'ApiNetworkError';
        this.friendlyMessage = friendlyMessage;
        this.cause = cause;
        if (cause instanceof Error && cause.stack) this.stack = cause.stack;
    }
}

/**
 * Normaliza o caminho para agrupar erros no Sentry: `/lancamentos/abc-123` e
 * `/lancamentos/def-456` sao a MESMA falha, e sem isto virariam dois grupos
 * distintos (alem de estourar o limite de cardinalidade das tags).
 */
function endpointPattern(endpoint: string): string {
    return endpoint
        .split('?')[0]
        .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
        .replace(/\/c[a-z0-9]{20,}/gi, '/:id')
        .replace(/\/\d+/g, '/:id');
}

/**
 * Reporta resposta de erro da API.
 *
 * So 5xx vira evento. 4xx e uso esperado (validacao, permissao, sessao
 * expirada) e ja fica registrado como breadcrumb pelo SDK: transformar isso em
 * evento encheria o painel de ruido justamente no dia de pico.
 */
function reportApiFailure(method: string, endpoint: string, status: number, message?: string): void {
    if (status < 500) return;

    const pattern = endpointPattern(endpoint);

    Sentry.withScope((scope) => {
        scope.setTag('api_endpoint', pattern);
        scope.setTag('api_status', String(status));
        scope.setContext('request', { method, endpoint, status, message });
        Sentry.captureException(new Error(`API ${status} ${method} ${pattern}`));
    });
}

// Ao descarregar a pagina o browser cancela os fetch em voo e eles rejeitam
// com o mesmo `Failed to fetch` de uma queda real. Nao e falha da API.
let pageUnloading = false;
if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => { pageUnloading = true; });
    window.addEventListener('pageshow', () => { pageUnloading = false; });
}

/**
 * Reporta falha de rede (API fora do ar, DNS, CORS, timeout). Nestes casos o
 * `fetch` rejeita sem resposta, entao nao ha status para inspecionar.
 *
 * Chamado so depois de esgotar as retentativas do fetchResilient. Vai como
 * warning e com fingerprint fixo por tipo + rota (ver networkFailure.ts):
 * sem isso cada chamador abria uma issue nova no Sentry (MAN-116). Browser
 * offline, aba fechando e requisicao abortada nao sao reportados.
 */
function reportNetworkFailure(method: string, endpoint: string, cause: unknown): void {
    const kind = classifyNetworkFailure(cause);
    if (!shouldReportNetworkFailure(kind, { online: isBrowserOnline(), unloading: pageUnloading })) return;

    const pattern = endpointPattern(endpoint);

    Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setFingerprint(networkFailureFingerprint(kind, pattern));
        scope.setTag('api_endpoint', pattern);
        scope.setTag('api_status', kind);
        scope.setContext('request', { method, endpoint });
        Sentry.captureException(
            cause instanceof Error ? cause : new Error(`Falha de rede em ${method} ${pattern}`),
        );
    });
}

interface RequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    headers?: Record<string, string>;
    timeoutMs?: number;
}

/** Uso de lançamentos no plano Gratuito (MAN-364): barra de contagem e bloqueio. */
export interface TransactionUsage {
    freePlan: boolean;
    used: number;
    limit: number | null;
    reached: boolean;
}

/**
 * Evento disparado quando lançamentos são criados ou apagados, para a barra do
 * plano Gratuito recontar sem esperar a próxima troca de tela.
 */
export const LANCAMENTOS_ALTERADOS_EVENT = 'lancamentos:alterados';

function avisarLancamentosAlterados<T>(resultado: T): T {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(LANCAMENTOS_ALTERADOS_EVENT));
    return resultado;
}

class ApiService {
    private baseUrl: string;
    private isRefreshing = false;
    private refreshSubscribers: Array<(token: string) => void> = [];

    // Circuit breaker: evita continuar martelando uma API fora do ar
    // (e gerando um evento novo no Sentry a cada tentativa) enquanto ela
    // nao mostra sinal de recuperacao.
    private consecutiveNetworkFailures = 0;
    private circuitOpenUntil = 0;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    private isCircuitOpen(): boolean {
        return Date.now() < this.circuitOpenUntil;
    }

    private async fetchWithTimeout(url: string, options: RequestInit, timeoutMs = REQUEST_TIMEOUT_MS): Promise<Response> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        try {
            return await fetch(url, { ...options, signal: controller.signal });
        } catch (err) {
            if (controller.signal.aborted) {
                throw new RequestTimeoutError(timeoutMs, url);
            }
            throw err;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    /**
     * `fetch` com timeout, circuit breaker e retry com backoff exponencial
     * (so para requisicoes idempotentes/GET). So propaga `ApiNetworkError`
     * quando o `fetch` de fato falhou (rede/DNS/timeout/CORS) apos esgotar
     * as tentativas ou quando o circuito esta aberto; respostas HTTP (mesmo
     * 5xx) continuam retornando normalmente para o tratamento existente.
     */
    private async fetchResilient(method: string, endpoint: string, url: string, fetchOptions: RequestInit, timeoutMs = REQUEST_TIMEOUT_MS): Promise<Response> {
        if (this.isCircuitOpen()) {
            throw new ApiNetworkError(null, 'Servidor indisponível no momento. Tentando novamente em instantes.');
        }

        const isIdempotent = method === 'GET';
        const maxAttempts = isIdempotent ? MAX_RETRIES + 1 : 1;
        let lastError: unknown;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const response = await this.fetchWithTimeout(url, fetchOptions, timeoutMs);
                if (isIdempotent && RETRYABLE_STATUS.has(response.status) && attempt < maxAttempts) {
                    await sleep(backoffDelay(attempt));
                    continue;
                }
                this.consecutiveNetworkFailures = 0;
                this.circuitOpenUntil = 0;
                return response;
            } catch (err) {
                lastError = err;
                if (isIdempotent && attempt < maxAttempts) {
                    await sleep(backoffDelay(attempt));
                    continue;
                }
            }
        }

        this.consecutiveNetworkFailures++;
        if (this.consecutiveNetworkFailures >= CIRCUIT_FAILURE_THRESHOLD) {
            this.circuitOpenUntil = Date.now() + CIRCUIT_COOLDOWN_MS;
        }
        reportNetworkFailure(method, endpoint, lastError);
        throw new ApiNetworkError(lastError);
    }

    private getAuthToken(): string | null {
        return safeLocalStorage.getItem('accessToken');
    }

    private getRefreshToken(): string | null {
        return safeLocalStorage.getItem('refreshToken');
    }

    private getFilialId(): string | null {
        try {
            const filialAtiva = safeLocalStorage.getItem('filialAtiva');
            return filialAtiva ? JSON.parse(filialAtiva)?.id : null;
        } catch {
            return null;
        }
    }

    /** Empresa ativa (ver contexts/empresaAtiva.ts). Sem ela o backend usa a propria. */
    private getEmpresaId(): string | null {
        return empresaSalva(safeLocalStorage.getItem(EMPRESA_ATIVA_KEY));
    }

    /** Cabecalhos de contexto: negocio e empresa em que a requisicao opera. */
    private contextHeaders(): Record<string, string> {
        const headers: Record<string, string> = { 'x-filial-id': this.getFilialId() || 'all' };
        const empresaId = this.getEmpresaId();
        if (empresaId) headers['x-empresa-id'] = empresaId;
        return headers;
    }

    /**
     * Bloqueios de cobranca no meio da sessao. Empresa suspensa ou inativa vale
     * so para a empresa ativa (o AuthContext marca so ela); ACCOUNT_SUSPENDED e a
     * conta inteira, quando a pessoa nao tem outra empresa liberada.
     */
    private avisarBloqueio(status: number, body: { code?: string; souDono?: boolean } | null): void {
        const empresaId = this.getEmpresaId();
        if (status === 402) {
            if (body?.code === 'EMPRESA_SUSPENSA' && empresaId) {
                window.dispatchEvent(new CustomEvent('empresa-bloqueada', { detail: { empresaId, estado: 'suspensa' } }));
            } else {
                window.dispatchEvent(new CustomEvent('account-suspended'));
            }
        } else if (status === 403 && body?.code === 'EMPRESA_INATIVA' && empresaId) {
            window.dispatchEvent(new CustomEvent('empresa-bloqueada', { detail: { empresaId, estado: 'inativa' } }));
        }
    }

    private clearSessionAndRedirect() {
        safeLocalStorage.removeItem('accessToken');
        safeLocalStorage.removeItem('refreshToken');
        this.clearCache();
        if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
        }
    }

    /**
     * Chave de cache inclui a filial e a empresa ativas: sem isso, trocar de
     * filial (ou de empresa, em "Todos os negocios") e cair a rede poderia
     * mostrar dados em cache do lugar ERRADO.
     */
    private cacheKey(endpoint: string): string {
        return `${CACHE_PREFIX}${this.getEmpresaId() || '-'}:${this.getFilialId() || 'all'}:${endpoint}`;
    }

    private saveToCache(endpoint: string, data: unknown): void {
        try {
            localStorage.setItem(this.cacheKey(endpoint), JSON.stringify({ data, timestamp: Date.now() }));
        } catch {
            // localStorage cheio ou indisponivel: cache e so um extra de
            // resiliencia, nao pode quebrar o fluxo principal.
        }
    }

    /**
     * Le do cache local. Retorna null se nao existe, estiver corrompido ou
     * tiver passado de CACHE_MAX_AGE_MS (para nao mostrar dado velho demais
     * como se fosse atual).
     */
    private readFromCache<T>(endpoint: string): T | null {
        try {
            const raw = localStorage.getItem(this.cacheKey(endpoint));
            if (!raw) return null;
            const { data, timestamp } = JSON.parse(raw) as { data: T; timestamp: number };
            if (Date.now() - timestamp > CACHE_MAX_AGE_MS) return null;
            return data;
        } catch {
            return null;
        }
    }

    private clearCache(): void {
        try {
            Object.keys(localStorage)
                .filter((key) => key.startsWith(CACHE_PREFIX))
                .forEach((key) => localStorage.removeItem(key));
        } catch {
            // ignore
        }
    }

    private subscribeTokenRefresh(callback: (token: string) => void) {
        this.refreshSubscribers.push(callback);
    }

    private onTokenRefreshed(token: string) {
        this.refreshSubscribers.forEach(callback => callback(token));
        this.refreshSubscribers = [];
    }

    private async tryRefreshToken(): Promise<string | null> {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) return null;

        try {
            const response = await fetch(`${this.baseUrl}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken }),
            });

            if (!response.ok) {
                throw new Error('Refresh failed');
            }

            const data = await response.json();
            safeLocalStorage.setItem('accessToken', data.accessToken);
            safeLocalStorage.setItem('refreshToken', data.refreshToken);
            return data.accessToken;
        } catch {
            this.clearSessionAndRedirect();
            return null;
        }
    }

    private async request<T>(endpoint: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
        const { method = 'GET', body, headers = {}, timeoutMs } = options;

        const token = this.getAuthToken();
        const defaultHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        if (token) {
            defaultHeaders['Authorization'] = `Bearer ${token}`;
        }

        Object.assign(defaultHeaders, this.contextHeaders());

        // fetchResilient cobre timeout, retry com backoff (GET) e circuit
        // breaker; so lanca (ApiNetworkError) quando o fetch realmente falhou
        // e o Sentry ja foi notificado la dentro.
        let response: Response;
        try {
            response = await this.fetchResilient(method, endpoint, `${this.baseUrl}${endpoint}`, {
                method,
                headers: { ...defaultHeaders, ...headers },
                body: body ? JSON.stringify(body) : undefined,
            }, timeoutMs);
        } catch (err) {
            // Rede/API totalmente fora do ar (ou circuito aberto): para GET,
            // tenta servir a ultima resposta boa em cache em vez de quebrar a
            // tela. Escritas (POST/PUT/PATCH/DELETE) nunca usam cache.
            if (err instanceof ApiNetworkError && method === 'GET') {
                const cached = this.readFromCache<T>(endpoint);
                if (cached !== null) {
                    window.dispatchEvent(new CustomEvent('api-cache-fallback', { detail: { endpoint } }));
                    return cached;
                }
            }
            throw err;
        }

        if (response.status === 401 && !endpoint.startsWith('/auth/')) {
            // Já reenviamos após um refresh bem-sucedido e ainda deu 401: não é
            // token expirado (ex.: conta inativa/removida). Encerra a sessão em vez
            // de refazer o refresh em loop.
            if (isRetry) {
                this.clearSessionAndRedirect();
                throw new Error('SESSION_EXPIRED');
            }

            if (!this.isRefreshing) {
                this.isRefreshing = true;
                const newToken = await this.tryRefreshToken();
                this.isRefreshing = false;

                if (newToken) {
                    this.onTokenRefreshed(newToken);
                    return this.request<T>(endpoint, options, true);
                }
                // refresh falhou: tryRefreshToken já limpou a sessão e redirecionou
                throw new Error('SESSION_EXPIRED');
            } else {
                return new Promise((resolve) => {
                    this.subscribeTokenRefresh(() => {
                        resolve(this.request<T>(endpoint, options, true));
                    });
                });
            }
        }

        // 402 Payment Required → empresa ativa (ou a conta inteira) suspensa por inadimplência
        if (response.status === 402) {
            this.avisarBloqueio(402, await response.json().catch(() => null));
            throw new Error('ACCOUNT_SUSPENDED');
        }

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
            this.avisarBloqueio(response.status, error);
            reportApiFailure(method, endpoint, response.status, error.message);
            throw new Error(error.message || `HTTP ${response.status}`);
        }

        const data = await response.json();
        if (method === 'GET') {
            this.saveToCache(endpoint, data);
        }
        return data;
    }

    private async requestMultipart<T>(endpoint: string, formData: FormData, method: 'POST' | 'PUT' | 'PATCH' = 'POST', isRetry = false): Promise<T> {
        const token = this.getAuthToken();
        const headers: Record<string, string> = {};

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        Object.assign(headers, this.contextHeaders());

        const response = await this.fetchResilient(method, endpoint, `${this.baseUrl}${endpoint}`, {
            method,
            headers,
            body: formData,
        });

        if (response.status === 401 && !endpoint.startsWith('/auth/')) {
            if (isRetry) {
                this.clearSessionAndRedirect();
                throw new Error('SESSION_EXPIRED');
            }

            if (!this.isRefreshing) {
                this.isRefreshing = true;
                const newToken = await this.tryRefreshToken();
                this.isRefreshing = false;

                if (newToken) {
                    this.onTokenRefreshed(newToken);
                    return this.requestMultipart<T>(endpoint, formData, method, true);
                }
                throw new Error('SESSION_EXPIRED');
            } else {
                return new Promise((resolve) => {
                    this.subscribeTokenRefresh(() => {
                        resolve(this.requestMultipart<T>(endpoint, formData, method, true));
                    });
                });
            }
        }

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
            this.avisarBloqueio(response.status, error);
            reportApiFailure(method, endpoint, response.status, error.message);
            throw new Error(error.message || `HTTP ${response.status}`);
        }

        return response.json();
    }

    private async downloadFile(endpoint: string, filename: string): Promise<void> {
        const token = this.getAuthToken();
        const headers: Record<string, string> = {};

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        Object.assign(headers, this.contextHeaders());

        const response = await this.fetchResilient('GET', endpoint, `${this.baseUrl}${endpoint}`, { headers });

        if (!response.ok) {
            reportApiFailure('GET', endpoint, response.status, 'falha ao baixar arquivo');
            throw new Error(`Erro ao baixar arquivo: HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }

    async login(email: string, password: string) {
        return this.request<{ accessToken: string; refreshToken: string; user: any }>('/auth/login', {
            method: 'POST',
            body: { email, password },
        });
    }

    async register(data: { name: string; email: string; password: string; whatsapp: string }) {
        return this.request<{ accessToken: string; refreshToken: string; user: any }>('/auth/register', {
            method: 'POST',
            body: data,
        });
    }

    async getProfile() {
        return this.request<any>('/auth/profile');
    }

    /** Matriz de permissões efetivas do usuário na filial ativa (ou consolidada). */
    async getPermissions() {
        return this.request<EffectivePermissionsMap>('/auth/permissions');
    }

    async getMyPaymentHistory() {
        return this.request<{
            totalPaid: number;
            count: number;
            status: string | null;
            invoices: Array<{
                id: string;
                date: string;
                planName: string;
                amount: number;
                type: 'CREATED' | 'RENEWED' | 'UPGRADED' | 'DOWNGRADED';
                transactionId: string | null;
                periodEnd: string | null;
            }>;
        }>('/subscriptions/my/payment-history');
    }

    async getMySubscriptionStatus() {
        return this.request<{
            hasSubscription: boolean;
            status?: string;
            paymentMethod?: 'pix' | 'card' | null;
            currentPeriodEnd?: string | null;
            daysUntilExpiry?: number | null;
            planName?: string | null;
        }>('/subscriptions/my/status');
    }

    async getTransactionUsage() {
        return this.request<TransactionUsage>('/subscriptions/my/usage');
    }

    async getMyCurrentCharge() {
        return this.request<{
            paymentMethod: 'pix' | 'card' | null;
            status: string;
            amount?: number;
            currency?: string;
            cycle?: number;
            pix?: { qrCode?: string; copyPaste?: string; expiresAt?: string };
            card?: { brand?: string; last4?: string };
            nextBillingDate?: string | null;
            source: 'pap' | 'cache' | 'none';
        }>('/subscriptions/my/current-charge');
    }

    async getNotifications() {
        return this.request<Array<{
            id: string;
            title: string;
            body: string;
            link: string | null;
            isRead: boolean;
            createdAt: string;
            announcementId: string | null;
        }>>('/notifications');
    }

    async getNotificationContent(id: string) {
        return this.request<{
            id: string;
            title: string;
            contentHtml: string;
            sentAt: string | null;
        }>(`/notifications/${id}/content`);
    }

    async markNotificationRead(id: string) {
        return this.request<unknown>(`/notifications/${id}/read`, { method: 'PATCH' });
    }

    async markAllNotificationsRead() {
        return this.request<unknown>('/notifications/read-all', { method: 'PATCH' });
    }

    async getOnboardingConfig() {
        return this.request<{ videoUrl: string | null }>('/auth/onboarding');
    }

    async markOnboardingComplete() {
        return this.request<{ ok: boolean }>('/auth/onboarding-complete', { method: 'POST' });
    }

    async getOnboardingSurveyQuestions() {
        return this.request<Record<string, Array<{ value: string; label: string }>>>('/onboarding-survey/questions');
    }

    async getOnboardingSurveyStatus() {
        return this.request<{ required: boolean; completed: boolean; completedAt: string | null }>('/onboarding-survey/status');
    }

    async submitOnboardingSurvey(data: {
        objetivo: string[];
        objetivoOutro?: string;
        controleFinanceiro: string;
        controleFinanceiroOutro?: string;
        segmento: string;
        segmentoOutro?: string;
        faturamentoAnual: string;
        numeroFuncionarios: string;
        comoConheceu: string[];
        comoConheceuOutro?: string;
    }) {
        return this.request<{ ok: boolean }>('/onboarding-survey', { method: 'POST', body: data });
    }

    async refreshToken(refreshToken: string) {
        return this.request<{ accessToken: string; refreshToken: string; user: any }>('/auth/refresh', {
            method: 'POST',
            body: { refreshToken },
        });
    }

    async forgotPassword(email: string) {
        return this.request<{ message: string }>('/auth/forgot-password', {
            method: 'POST',
            body: { email },
        });
    }

    async resetPassword(token: string, newPassword: string) {
        return this.request<{ message: string }>('/auth/reset-password', {
            method: 'POST',
            body: { token, newPassword },
        });
    }

    async getReport(params: {
        type: string;
        startDate?: string;
        endDate?: string;
        granularity?: 'day' | 'month' | 'year';
        categoriaLabel?: string;
        subcategoriaLabel?: string;
        formaPagamento?: string;
        filialIds?: string[];
    }) {
        const searchParams = new URLSearchParams();
        searchParams.set('type', params.type);
        if (params.startDate) searchParams.set('startDate', params.startDate);
        if (params.endDate) searchParams.set('endDate', params.endDate);
        if (params.granularity) searchParams.set('granularity', params.granularity);
        if (params.categoriaLabel) searchParams.set('categoriaLabel', params.categoriaLabel);
        if (params.subcategoriaLabel) searchParams.set('subcategoriaLabel', params.subcategoriaLabel);
        if (params.formaPagamento) searchParams.set('formaPagamento', params.formaPagamento);
        if (params.filialIds && params.filialIds.length > 0) searchParams.set('filialIds', params.filialIds.join(','));

        return this.request<any>(`/reports?${searchParams.toString()}`);
    }

    async exportReportExcel(params: { type: string; startDate?: string; endDate?: string }) {
        const searchParams = new URLSearchParams();
        searchParams.set('type', params.type);
        if (params.startDate) searchParams.set('startDate', params.startDate);
        if (params.endDate) searchParams.set('endDate', params.endDate);

        const filename = `relatorio-${params.type}-${Date.now()}.xlsx`;
        await this.downloadFile(`/reports/export/excel?${searchParams.toString()}`, filename);
    }

    async exportReportPdf(params: { type: string; startDate?: string; endDate?: string }) {
        const searchParams = new URLSearchParams();
        searchParams.set('type', params.type);
        if (params.startDate) searchParams.set('startDate', params.startDate);
        if (params.endDate) searchParams.set('endDate', params.endDate);

        const filename = `relatorio-${params.type}-${Date.now()}.pdf`;
        await this.downloadFile(`/reports/export/pdf?${searchParams.toString()}`, filename);
    }

    async getPaymentMethods() {
        return this.request<any[]>('/chart-of-accounts/payment-methods');
    }

    async createPaymentMethod(data: any) {
        return this.request<any>('/chart-of-accounts/payment-methods', { method: 'POST', body: data });
    }

    async updatePaymentMethod(id: string, data: any) {
        return this.request<any>(`/chart-of-accounts/payment-methods/${id}`, { method: 'PATCH', body: data });
    }

    async deletePaymentMethod(id: string) {
        return this.request<void>(`/chart-of-accounts/payment-methods/${id}`, { method: 'DELETE' });
    }

    async getChartOfAccounts() {
        return this.request<any[]>('/chart-of-accounts/accounts');
    }

    async getChartOfAccount(id: string) {
        return this.request<any>(`/chart-of-accounts/accounts/${id}`);
    }

    async createChartOfAccount(data: any) {
        return this.request<any>('/chart-of-accounts/accounts', { method: 'POST', body: data });
    }

    async updateChartOfAccount(id: string, data: any) {
        return this.request<any>(`/chart-of-accounts/accounts/${id}`, { method: 'PATCH', body: data });
    }

    async deleteChartOfAccount(id: string) {
        return this.request<void>(`/chart-of-accounts/accounts/${id}`, { method: 'DELETE' });
    }

    async getCashFlowYears() {
        return this.request<number[]>('/cashflow/years');
    }

    async getCashFlowMonthly(ano?: number) {
        const params = ano ? `?ano=${ano}` : '';
        return this.request<{
            ano: number;
            meses: Array<{
                mes: number;
                mesNome: string;
                receita: number;
                despesa: number;
                saldo: number;
                acumulado: number;
            }>;
            totais: { receita: number; despesa: number; saldo: number };
        }>(`/cashflow/monthly${params}`);
    }

    async getCashFlowTransactions(params?: { startDate?: string; endDate?: string; type?: string }) {
        const searchParams = new URLSearchParams();
        if (params?.startDate) searchParams.set('startDate', params.startDate);
        if (params?.endDate) searchParams.set('endDate', params.endDate);
        if (params?.type) searchParams.set('type', params.type);
        const query = searchParams.toString();
        return this.request<any[]>(`/cashflow/transactions${query ? `?${query}` : ''}`);
    }

    async getCashFlowCategories() {
        return this.request<string[]>('/cashflow/categories');
    }

    async getCashFlowFluxo(dataInicio: string, dataFim: string) {
        type FluxoCat = { nome: string; total: number[]; subcategorias: { nome: string; valores: number[] }[] };
        return this.request<{
            meses: string[];
            entradas: { total: number[]; categorias: FluxoCat[] };
            saidas: { total: number[]; categorias: FluxoCat[] };
            saldo: number[];
        }>(`/cashflow/fluxo?dataInicio=${dataInicio}&dataFim=${dataFim}`);
    }

    async getCashFlowDre(dataInicio: string, dataFim: string) {
        return this.request<{
            meses: string[];
            linhas: Array<{
                tipo: 'grupo' | 'subtotal' | 'resultado';
                nome: string;
                sinal: string;
                valores: number[];
                subCategorias?: Array<{ nome: string; valores: number[] }>;
            }>;
        }>(`/cashflow/dre?dataInicio=${dataInicio}&dataFim=${dataFim}`);
    }



    async getDashboardSummary(dataInicio?: string, dataFim?: string) {
        const params = new URLSearchParams();
        if (dataInicio) params.set('dataInicio', dataInicio);
        if (dataFim) params.set('dataFim', dataFim);
        const query = params.toString();
        return this.request<any>(`/dashboard/summary${query ? `?${query}` : ''}`);
    }

    async getDashboardRecentTransactions() {
        return this.request<any[]>('/dashboard/recent-transactions');
    }

    async searchTransactions(params: { q?: string; dataInicio?: string; dataFim?: string; page?: number; limit?: number }) {
        const qs = new URLSearchParams();
        if (params.q) qs.set('q', params.q);
        if (params.dataInicio) qs.set('dataInicio', params.dataInicio);
        if (params.dataFim) qs.set('dataFim', params.dataFim);
        if (params.page) qs.set('page', String(params.page));
        if (params.limit) qs.set('limit', String(params.limit));
        const query = qs.toString();
        return this.request<any>(`/dashboard/search-transactions${query ? `?${query}` : ''}`);
    }

    async getDashboardCashFlow(dataInicio?: string, dataFim?: string) {
        const params = new URLSearchParams();
        if (dataInicio) params.set('dataInicio', dataInicio);
        if (dataFim) params.set('dataFim', dataFim);
        const query = params.toString();
        return this.request<any[]>(`/dashboard/cash-flow${query ? `?${query}` : ''}`);
    }

    async getDistribuicaoCategorias(tipo: 'GASTOS' | 'RECEITA', dataInicio?: string, dataFim?: string, groupBy?: 'subcategoria') {
        const params = new URLSearchParams({ tipo });
        if (dataInicio) params.set('dataInicio', dataInicio);
        if (dataFim) params.set('dataFim', dataFim);
        if (groupBy) params.set('groupBy', groupBy);
        return this.request<any[]>(`/dashboard/distribuicao-categorias?${params.toString()}`);
    }



    async getFaqs() {
        return this.request<any[]>('/faq');
    }

    async getFaq(id: string) {
        return this.request<any>(`/faq/${id}`);
    }

    async searchFaqs(query: string) {
        return this.request<any[]>(`/faq/search?q=${encodeURIComponent(query)}`);
    }

    async getLancamentos(params?: {
        categoria?: string;
        mes?: number;
        ano?: number;
        subCategoria?: string;
        formaPagamento?: string;
        dataInicio?: string;
        dataFim?: string;
        page?: number;
        limit?: number;
        categoriaLabel?: string;
        subCategoriaLabel?: string;
        filialIds?: string;
        orderBy?: string;
    }) {
        const searchParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== '') {
                    searchParams.append(key, String(value));
                }
            });
        }
        const query = searchParams.toString();
        return this.request<{ data: any[]; pagination: any }>(`/lancamentos${query ? `?${query}` : ''}`);
    }

    async getLancamento(id: string) {
        return this.request<any>(`/lancamentos/${id}`);
    }

    async createLancamento(data: {
        categoria: string;
        data: string;
        subCategoria: string;
        formaPagamento: string;
        valor: number;
        informacoesAdicionais?: string | null;
    }) {
        return this.request<any>('/lancamentos', { method: 'POST', body: data }).then(avisarLancamentosAlterados);
    }

    async updateLancamento(id: string, data: Partial<{
        categoria: string;
        data: string;
        subCategoria: string;
        formaPagamento: string;
        valor: number;
        informacoesAdicionais?: string | null;
    }>) {
        return this.request<any>(`/lancamentos/${id}`, { method: 'PUT', body: data });
    }

    async deleteLancamento(id: string) {
        return this.request<any>(`/lancamentos/${id}`, { method: 'DELETE' }).then(avisarLancamentosAlterados);
    }

    async deleteUltimoLancamento() {
        return this.request<any>('/lancamentos/ultimo', { method: 'DELETE' }).then(avisarLancamentosAlterados);
    }

    async getLancamentosSummary(params?: Record<string, any>) {
        const searchParams = new URLSearchParams();
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== '') {
                    searchParams.append(key, String(value));
                }
            });
        }
        const query = searchParams.toString();
        return this.request<{ totalReceita: number; totalGastos: number; resultado: number; totalLancamentos: number }>(
            `/lancamentos/summary${query ? `?${query}` : ''}`
        );
    }

    async getLancamentosSubcategorias() {
        return this.request<string[]>('/lancamentos/subcategorias');
    }

    async getLancamentosFormasPagamento() {
        return this.request<string[]>('/lancamentos/formas-pagamento');
    }
    async getLancamentosAnos() {
        return this.request<number[]>('/lancamentos/anos');
    }

    async previewImportLancamentos(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        return this.requestMultipart<ImportPreviewResult>('/imports/lancamentos/preview', formData);
    }

    async getPendingImportLancamentos() {
        return this.request<ImportPreviewResult | null>('/imports/lancamentos/pending');
    }

    async confirmImportLancamentos(data: {
        batchPreviewId: string;
        conflictResolution?: 'SIGN' | 'CATEGORY';
        ignoreDuplicates?: boolean;
        keepDuplicateRowNumbers?: number[];
        resolutions?: { subCategoryTypes?: Record<string, 'ENTRADA' | 'SAIDA'> };
        mapping?: ImportReconciliationMapping;
    }) {
        return this.request<{ batchId: string; totalsCriados: number; entidadesCriadas: { categorias: number; subCategorias: number; formasPagamento: number }; skippedDuplicates: number }>(
            '/imports/lancamentos/confirm',
            { method: 'POST', body: data, timeoutMs: IMPORT_CONFIRM_TIMEOUT_MS },
        ).then(avisarLancamentosAlterados);
    }

    async getImportLancamentosRows(batchId: string) {
        return this.request<{
            batchId: string;
            fileName: string;
            summary: ImportPreviewSummary;
            rows: ImportPreviewRow[];
            discardedRows: ImportDiscardedRow[];
        }>(`/imports/lancamentos/${batchId}/rows`);
    }

    async deleteImportLancamentosBatch(batchId: string) {
        return this.request<{ deleted: number; batchId: string }>(`/imports/lancamentos/${batchId}`, { method: 'DELETE' });
    }

    async fixImportLancamentosRows(batchId: string, corrections: ImportRowCorrection[]) {
        return this.request<ImportPreviewResult>(`/imports/lancamentos/${batchId}/rows`, {
            method: 'PATCH',
            body: { corrections },
        });
    }

    /** Histórico de importações da filial ativa. */
    async listImportBatches() {
        return this.request<ImportBatchSummary[]>('/imports/lancamentos');
    }

    async getImportBatchDetails(batchId: string) {
        return this.request<ImportBatchDetails>(`/imports/lancamentos/${batchId}/details`);
    }

    /**
     * Reverter: apaga os lançamentos do lote e o que ele criou no plano de
     * contas que ninguém mais usa, mantendo o registro no histórico.
     */
    async revertImportBatch(batchId: string) {
        return this.request<{
            deleted: number;
            batchId: string;
            planoDeContas: {
                categorias: number;
                subCategorias: number;
                formasPagamento: number;
                mantidasEmUso: number;
                naoRastreado: boolean;
            };
        }>(`/imports/lancamentos/${batchId}`, { method: 'DELETE' }).then(avisarLancamentosAlterados);
    }

    /** Excluir do histórico. O backend recusa se o lote ainda não foi revertido. */
    async destroyImportBatch(batchId: string) {
        return this.request<{ batchId: string; deleted: boolean }>(`/imports/lancamentos/${batchId}/record`, { method: 'DELETE' });
    }

    async downloadImportBatchXlsx(batchId: string, fileName: string) {
        await this.downloadFile(`/imports/lancamentos/${batchId}/export/xlsx`, `importacao-${fileName}.xlsx`);
    }

    async downloadImportBatchPdf(batchId: string, fileName: string) {
        await this.downloadFile(`/imports/lancamentos/${batchId}/export/pdf`, `importacao-${fileName}.pdf`);
    }

    async downloadImportLancamentosDiscardedRows(batchId: string) {
        await this.downloadFile(`/imports/lancamentos/${batchId}/discarded-rows/export`, `linhas-com-erro-${batchId}.xlsx`);
    }

    async getPlanoCategorias(): Promise<{ label: string; subCategorias: string[] }[]> {
        const categorias = await this.getCategorias();
        return (categorias as any[]).map((c) => ({
            label: c.nome,
            subCategorias: (c.subCategorias ?? []).map((s: any) => s.nome),
        }));
    }

    async getPlanoContasCompleto() {
        return this.request<{
            categorias: any[];
            formasPagamento: any[];
            resumo: {
                totalGrupos: number;
                totalCategorias: number;
                totalFormasPagamento: number;
                categoriasEntrada: number;
                categoriasSaida: number;
                gruposEntrada: number;
                gruposSaida: number;
            };
        }>('/plano-contas');
    }

    async getCategorias(tipo?: string) {
        const params = tipo ? `?tipo=${tipo}` : '';
        return this.request<any[]>(`/plano-contas/categorias${params}`);
    }

    async getCategoria(id: string) {
        return this.request<any>(`/plano-contas/categorias/${id}`);
    }

    async createCategoria(data: { tipo: string; nome: string; descricao?: string; ordem?: number }) {
        return this.request<any>('/plano-contas/categorias', { method: 'POST', body: data });
    }

    async updateCategoria(id: string, data: Partial<{ tipo: string; nome: string; descricao?: string; ordem?: number; ativo?: boolean }>) {
        return this.request<any>(`/plano-contas/categorias/${id}`, { method: 'PATCH', body: data });
    }

    async deleteCategoria(id: string) {
        return this.request<void>(`/plano-contas/categorias/${id}`, { method: 'DELETE' });
    }

    async getSubCategorias(params?: { categoriaId?: string; tipo?: string }) {
        const searchParams = new URLSearchParams();
        if (params?.categoriaId) searchParams.set('categoriaId', params.categoriaId);
        if (params?.tipo) searchParams.set('tipo', params.tipo);
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        return this.request<any[]>(`/plano-contas/subcategorias${query}`);
    }

    async getSubCategoria(id: string) {
        return this.request<any>(`/plano-contas/subcategorias/${id}`);
    }

    async createSubCategoria(data: { categoriaId: string; nome: string; descricao?: string; ordem?: number; personalizavel?: boolean }) {
        return this.request<any>('/plano-contas/subcategorias', { method: 'POST', body: data });
    }

    async updateSubCategoria(id: string, data: Partial<{ categoriaId?: string; codigo?: string; nome?: string; descricao?: string; ordem?: number; ativo?: boolean }>) {
        return this.request<any>(`/plano-contas/subcategorias/${id}`, { method: 'PATCH', body: data });
    }

    /** Salva em lote o rearranjo feito no modo "Organizar grupos" do Plano de Contas. */
    async moverSubCategorias(movimentacoes: { id: string; categoriaId: string; ordem: number }[]) {
        return this.request<{ movidas: number }>('/plano-contas/subcategorias/mover', {
            method: 'PATCH',
            body: { movimentacoes },
        });
    }

    async deleteSubCategoria(id: string) {
        return this.request<void>(`/plano-contas/subcategorias/${id}`, { method: 'DELETE' });
    }

    async getFormasPagamentoNorm(params?: { tipo?: string }) {
        const searchParams = new URLSearchParams();
        if (params?.tipo) searchParams.set('tipo', params.tipo);
        if (params?.tipo && params.tipo !== 'AMBOS') {
            const [tipoEspecifico, tipoAmbos] = await Promise.all([
                this.request<any[]>(`/plano-contas/formas-pagamento?tipo=${params.tipo}`),
                this.request<any[]>(`/plano-contas/formas-pagamento?tipo=AMBOS`),
            ]);
            return [...tipoEspecifico, ...tipoAmbos];
        }
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        return this.request<any[]>(`/plano-contas/formas-pagamento${query}`);
    }

    async getFormaPagamentoNorm(id: string) {
        return this.request<any>(`/plano-contas/formas-pagamento/${id}`);
    }

    async createFormaPagamentoNorm(data: { nome: string; descricao?: string; ordem?: number }) {
        return this.request<any>('/plano-contas/formas-pagamento', { method: 'POST', body: data });
    }

    async updateFormaPagamentoNorm(id: string, data: Partial<{ nome?: string; descricao?: string; ordem?: number; ativo?: boolean }>) {
        return this.request<any>(`/plano-contas/formas-pagamento/${id}`, { method: 'PATCH', body: data });
    }

    async deleteFormaPagamentoNorm(id: string) {
        return this.request<void>(`/plano-contas/formas-pagamento/${id}`, { method: 'DELETE' });
    }

    // Filiais
    async getMinhasFiliais() {
        return this.request<{ id: string; name: string; empresaId: string; isActive: boolean; role: 'ADMIN' | 'EDITOR' | 'VIEWER'; empresa: { id: string; name: string } }[]>('/filiais/minhas');
    }

    // Empresas do usuario e gestao de acessos do dono (plano de convites, Fase 2)
    async getMinhasEmpresas() {
        return this.request<EmpresaDoUsuario[]>('/empresas/minhas');
    }

    /** Cria a empresa propria de quem so participa de empresas de outros (plano padrao). */
    async criarMinhaEmpresa() {
        return this.request<{ empresaId: string }>('/empresas/minha', { method: 'POST' });
    }

    async getPerfisDaEmpresa(empresaId: string) {
        return this.request<AccessProfile[]>(`/empresas/${empresaId}/perfis`);
    }

    async criarPerfilDaEmpresa(empresaId: string, data: { name: string; description?: string; permissions: PermissionItem[] }) {
        return this.request<AccessProfile>(`/empresas/${empresaId}/perfis`, { method: 'POST', body: data });
    }

    async atualizarPerfilDaEmpresa(
        empresaId: string,
        perfilId: string,
        data: { name?: string; description?: string; permissions?: PermissionItem[] },
    ) {
        return this.request<AccessProfile>(`/empresas/${empresaId}/perfis/${perfilId}`, { method: 'PATCH', body: data });
    }

    async duplicarPerfilDaEmpresa(empresaId: string, perfilId: string) {
        return this.request<AccessProfile>(`/empresas/${empresaId}/perfis/${perfilId}/duplicar`, { method: 'POST' });
    }

    async excluirPerfilDaEmpresa(empresaId: string, perfilId: string) {
        return this.request<{ deleted: boolean }>(`/empresas/${empresaId}/perfis/${perfilId}`, { method: 'DELETE' });
    }

    async getMembrosDaEmpresa(empresaId: string) {
        return this.request<MembroDaEmpresa[]>(`/empresas/${empresaId}/membros`);
    }

    async atualizarMembroDaEmpresa(
        empresaId: string,
        userId: string,
        data: { accessProfileId?: string; overrides?: PermissionItem[]; filialIds?: string[] },
    ) {
        return this.request<{ ok: boolean }>(`/empresas/${empresaId}/membros/${userId}`, { method: 'PATCH', body: data });
    }

    async removerMembroDaEmpresa(empresaId: string, userId: string) {
        return this.request<{ removido: boolean }>(`/empresas/${empresaId}/membros/${userId}`, { method: 'DELETE' });
    }

    // Convites (plano de convites, Fase 3)
    async getConvitesDaEmpresa(empresaId: string) {
        return this.request<ConviteDaEmpresa[]>(`/empresas/${empresaId}/convites`);
    }

    async convidarParaEmpresa(empresaId: string, data: { email: string; accessProfileId?: string; filialIds: string[] }) {
        return this.request<ConviteDaEmpresa>(`/empresas/${empresaId}/convites`, { method: 'POST', body: data });
    }

    async reenviarConvite(empresaId: string, conviteId: string) {
        return this.request<ConviteDaEmpresa>(`/empresas/${empresaId}/convites/${conviteId}/reenviar`, { method: 'POST' });
    }

    async cancelarConvite(empresaId: string, conviteId: string) {
        return this.request<{ revogado: boolean }>(`/empresas/${empresaId}/convites/${conviteId}`, { method: 'DELETE' });
    }

    async consultarConvite(token: string) {
        return this.request<ConvitePublico>('/convites/consultar', { method: 'POST', body: { token } });
    }

    async aceitarConvite(token: string) {
        return this.request<{ empresaId: string }>('/convites/aceitar', { method: 'POST', body: { token } });
    }

    async cadastrarPorConvite(data: { token: string; name: string; password: string }) {
        return this.request<Awaited<ReturnType<ApiService['login']>>>('/convites/cadastro', { method: 'POST', body: data });
    }

    async selecionarFilial(filialId: string) {
        return this.request<{ filialId: string; filialName: string }>('/filiais/selecionar', {
            method: 'POST',
            body: { filialId },
        });
    }

    async getEmpresas() {
        return this.request<any[]>('/filiais/empresas');
    }

    async createEmpresa(data: { name: string; cnpj?: string }) {
        return this.request<any>('/filiais/empresas', { method: 'POST', body: data });
    }

    async updateEmpresa(empresaId: string, data: { name?: string; cnpj?: string; isActive?: boolean }) {
        return this.request<any>(`/filiais/empresas/${empresaId}`, { method: 'PATCH', body: data });
    }

    async getFiliaisByEmpresa(empresaId: string) {
        return this.request<any[]>(`/filiais/empresa/${empresaId}`);
    }

    async createFilial(data: { name: string; empresaId: string; cnpj?: string; adminUserId?: string }) {
        return this.request<any>('/filiais', { method: 'POST', body: data });
    }

    async updateFilial(filialId: string, data: { name?: string; cnpj?: string; isActive?: boolean }) {
        return this.request<any>(`/filiais/${filialId}`, { method: 'PATCH', body: data });
    }

    async getUsuariosDaFilial(filialId: string) {
        return this.request<any[]>(`/filiais/${filialId}/usuarios`);
    }

    async associarUsuario(filialId: string, data: { userId: string; role?: 'ADMIN' | 'EDITOR' | 'VIEWER' }) {
        return this.request<any>(`/filiais/${filialId}/usuarios`, { method: 'POST', body: data });
    }

    async removerUsuario(filialId: string, userId: string) {
        return this.request<void>(`/filiais/${filialId}/usuarios/${userId}`, { method: 'DELETE' });
    }

    async desativarFilial(filialId: string) {
        return this.request<any>(`/filiais/${filialId}/desativar`, { method: 'PATCH' });
    }

    async ativarFilial(filialId: string) {
        return this.request<any>(`/filiais/${filialId}/ativar`, { method: 'PATCH' });
    }

    async deletarFilial(filialId: string) {
        return this.request<void>(`/filiais/${filialId}`, { method: 'DELETE' });
    }

    async updateCashFlowTransaction(id: string, data: Partial<{ description: string; amount: number; type: string; category: string; date: string; notes: string }>) {
        return this.request<any>(`/cashflow/transactions/${id}`, { method: 'PATCH', body: data });
    }

    async getUsuariosGestao() {
        return this.request<any[]>('/users');
    }

    async convidarUsuario(data: { name: string; email: string; role: string }) {
        return this.request<any>('/users/convidar', { method: 'POST', body: data });
    }

    async atualizarUsuarioStatus(id: string, status: string) {
        return this.request<any>(`/users/${id}/status`, { method: 'PATCH', body: { status } });
    }

    async atualizarUsuarioRole(id: string, role: string) {
        return this.request<any>(`/users/${id}/role`, { method: 'PATCH', body: { role } });
    }

    async updateProfile(data: { name?: string; email?: string }) {
        return this.request<any>('/auth/profile', { method: 'PATCH', body: data });
    }

    async changePassword(data: { currentPassword: string; newPassword: string }) {
        return this.request<{ message: string }>('/auth/change-password', { method: 'POST', body: data });
    }

    async deleteMyAccount(): Promise<void> {
        return this.request<void>('/auth/me', { method: 'DELETE' });
    }

    async getNotificationPrefs() {
        return this.request<Record<string, boolean>>('/auth/notification-prefs');
    }

    async updateNotificationPrefs(prefs: Record<string, boolean>) {
        return this.request<Record<string, boolean>>('/auth/notification-prefs', {
            method: 'PATCH',
            body: prefs,
        });
    }
}

export const api = new ApiService(API_BASE_URL);